import { ChevronDown } from "lucide-react";
import type React from "react";
import { useCallback, useState } from "react";

import { Button } from "@src/shared/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@src/shared/components/ui/collapsible";
import { cn } from "@src/shared/lib/utils";

export interface CollapsiblePanelProps {
  label: string;
  children: React.ReactNode;
  count?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  /**
   * When false the panel stays open and the collapse affordance is hidden. Use
   * for panels that own a whole tab, where collapsing has nothing to reveal.
   */
  collapsible?: boolean;
  /**
   * Header buttons shown between the label and the collapse chevron. They sit
   * outside the collapse trigger so pressing an action does not also toggle the
   * panel.
   */
  actions?: React.ReactNode;
}

function PanelHeaderLabel({
  label,
  count,
}: {
  label: string;
  count?: React.ReactNode;
}) {
  return (
    <>
      <span className="font-mono text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </span>
      {count != null ? (
        <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
          {count}
        </span>
      ) : null}
    </>
  );
}

export function CollapsiblePanel({
  label,
  children,
  count,
  open,
  defaultOpen = false,
  onOpenChange,
  className,
  contentClassName,
  contentStyle,
  collapsible = true,
  actions,
}: CollapsiblePanelProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isOpen = collapsible ? (open ?? uncontrolledOpen) : true;
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange],
  );

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setOpen}
      className="h-full min-h-0 w-full"
    >
      <div
        className={cn(
          "relative flex w-full shrink-0 flex-col overflow-hidden rounded-[10px] border border-border bg-background after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:z-10 after:border-border after:border-b after:content-['']",
          className,
        )}
      >
        <div className="flex h-9 w-full shrink-0 items-center gap-1 bg-surface pr-1.5">
          {collapsible ? (
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className="flex h-9 min-w-0 flex-1 justify-start gap-2 rounded-none bg-surface px-3 text-left hover:bg-surface"
              >
                <PanelHeaderLabel label={label} count={count} />
              </Button>
            </CollapsibleTrigger>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2 px-3">
              <PanelHeaderLabel label={label} count={count} />
            </div>
          )}
          {actions}
          {collapsible ? (
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 rounded-md text-muted-foreground"
                aria-label={isOpen ? `Collapse ${label}` : `Expand ${label}`}
              >
                <ChevronDown
                  className={cn(
                    "size-3.5 transition-transform",
                    isOpen && "rotate-180",
                  )}
                  aria-hidden="true"
                />
              </Button>
            </CollapsibleTrigger>
          ) : null}
        </div>
        <CollapsibleContent
          className={cn(isOpen && "min-h-0 flex-1 overflow-hidden")}
        >
          <div
            className={cn(isOpen && "h-full min-h-0", contentClassName)}
            style={contentStyle}
          >
            {children}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
